import { Injectable, effect, inject, signal } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, UrlSegment } from '@angular/router';
import { forkJoin, map } from 'rxjs';

import { authGuard } from '../guards/auth.guard';
import { claimGuard } from '../guards/claim.guard';
import { ApiService } from './api.service';
import { UserProduct, UserService } from './user.service';
import { StateService } from '../../products/services/state.service';
import { InstitutionType } from '../../products/services/institution.service';
import { ProductPageComponent } from '../../products/product-page/product-page/product-page.component';
import { PrintProductPageComponent } from '../../products/product-page/print/print-product-page/print-product-page.component';

export type NavItem = {
  name: string;
  path: string;
}

export type FlexItem = {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  tourId?: string;
  children: FlexItem[];
}

export type ProductComponent = {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  tourId?: string;
  children?: FlexItem[];
}

export interface PrintSettings {
  headerDateType?: string;
}

export interface Product {
  id: string;
  name: string;
  clientCode: string;
  hasDashboard: boolean;
  dashboardComponent: string;
  institutionType: InstitutionType;
  rooms: Room[];
}

export interface Room {
  name: string;
  clientCode: string;
  pages: Page[];
}

export interface Page {
  name: string;
  clientCode: string;
  printSettings?: PrintSettings;
  tiers?: string[];
  paywallMessage?: string;
  components: ProductComponent[];
}

export interface ProductPage {
  path: string;
  resolvedPath: string;
  product: string;
  room: string;
  page: string;
  name?: string;
  printSettings?: PrintSettings;
  tiers?: string[];
  paywallMessage?: string;
  components: ProductComponent[];
}

export interface ProductNav {
  name: string;
  path: string;
  rooms: NavItem[];
}

export interface RoomNav {
  name: string;
  path: string;
  pages: NavItem[];
}

export interface ProductKey {
  clientCode: string;
  institutionType: InstitutionType;
}

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private router = inject(Router);
  private apiService = inject(ApiService);
  
  readonly institution = inject(StateService).institution;
  readonly user = inject(UserService).state;
  readonly userProducts = inject(UserService).userProducts;
  readonly productsFetched = signal(false);
  readonly datesFetched = signal(false);

  private products: Map<string, Product> = new Map();

  constructor() {
    effect(() => {
      const userProducts = this.userProducts();
      this.registerRoutes(userProducts);
      this.getUserProducts(userProducts);
    });
  }

  private registerRoutes(products: UserProduct[]) {
    if (products) {
      products.forEach(product => {
        if (!this.router.config.find(r => r.path === product.clientCode)) {
          this.router.config.push(...this.getRoutes(product));
        }
      });
    }
  }

  private getRoutes(product: UserProduct): Route[] {
    if (product.clientCode == 'file-transfer') {
      return [
        { path: 'file-transfer', loadChildren: () => import('../../file-transfer/file-transfer.routes').then(m => m.FILE_TRANSFER_ROUTEES) }
      ]
    } else {
      return [
        { path: `${product.clientCode}`, component: ProductPageComponent, canActivate: [authGuard, claimGuard], data: { requiredClaim: `${product.clientCode}` } },
        { path: `${product.clientCode}/:room`, component: ProductPageComponent, canActivate: [authGuard, claimGuard], data: { requiredClaim: `${product.clientCode}` } },
        { path: `${product.clientCode}/:room/:page`, component: ProductPageComponent, canActivate: [authGuard, claimGuard], data: { requiredClaim: `${product.clientCode}` } },
        { path: `print/${product.clientCode}`, component: PrintProductPageComponent, canActivate: [authGuard, claimGuard], data: { requiredClaim: `${product.clientCode}` } },
        { path: `print/${product.clientCode}/:room`, component: PrintProductPageComponent, canActivate: [authGuard, claimGuard], data: { requiredClaim: `${product.clientCode}` } },
        { path: `print/${product.clientCode}/:room/:page`, component: PrintProductPageComponent, canActivate: [authGuard, claimGuard], data: { requiredClaim: `${product.clientCode}` } }
      ];
    }
  }

  private getUserProducts(products: UserProduct[]): void {
    forkJoin([
      this.apiService.get<Product>('/assets/products/peer-comparison-1.json').pipe(map(product => {
        this.products.set('peer-comparison-1', product);
      })),
      this.apiService.get<Product>('/assets/products/peer-comparison-2.json').pipe(map(product => {
        this.products.set('peer-comparison-2', product);
      })),
      this.apiService.get<Product>('/assets/products/non-maturity-deposits-1.json').pipe(map(product => {
        this.products.set('non-maturity-deposits-1', product);
      }))
    ]).subscribe(() => {
      this.productsFetched.set(true)
    });
  }

  private lookupProduct(clientCode: string): Product {
    const key = `${clientCode}-${this.institution().institutionType}`;
    return this.products.get(key) ?? {} as Product;
  }
  
  public getCurrentProduct(route: ActivatedRouteSnapshot): string {
    const segments = route.url.map(s => s.path);
    const productCode = segments[0];
    return productCode; 
  }

  public resolveProductPage(route: ActivatedRouteSnapshot): ProductPage | undefined {
    const segments = route.url.map(s => s.path);
    if (segments[0] == 'print') {
      segments.shift();
    }
    const productCode = segments[0];
    const product = this.lookupProduct(productCode);

    let productPage: ProductPage | undefined = undefined;

    let roomCode = route.params['room'];
    if (!roomCode) {
      roomCode = product.rooms[0].clientCode;
      this.router.navigate([`/${product.clientCode}/${roomCode}`]);
      return productPage;
    }

    let numPages = 0;
    let pageCode = route.params['page'];
    if (!pageCode) {
      pageCode = product.rooms.find(r => r.clientCode === roomCode)?.pages[0].clientCode;
      numPages = product.rooms.find(r => r.clientCode === roomCode)?.pages.length ?? 0;
      if (numPages > 1) {
        this.router.navigate([`/${product.clientCode}/${roomCode}/${pageCode}`]);
        return productPage;
      }
    }

    const room = product.rooms.find(r => r.clientCode === roomCode);
    const page = room?.pages.find(p => p.clientCode === pageCode);
    numPages = room?.pages.length ?? 0;

    productPage = {
      path: route.pathFromRoot
        .map(v => v.url.map(segment => segment.toString()).join('/'))
        .join('/'),
      resolvedPath: numPages == 1 ? `/${product.clientCode}/${roomCode}` : `/${product.clientCode}/${roomCode}/${pageCode}`,
      product: productCode,
      room: roomCode,
      page: pageCode,
      name: room?.name + ' Room' + (numPages > 1 ? ' - ' + page?.name : ''),
      printSettings: page?.printSettings,
      tiers: page?.tiers,
      paywallMessage: page?.paywallMessage,
      components: product.rooms.find(r => r.clientCode === roomCode)?.pages.find(p => p.clientCode === pageCode)?.components ?? []
    };

    return productPage;
  }

  public getProductNav(path: UrlSegment[]): ProductNav {
    if (path[0].path == 'print') {
      path.shift();
    }
    const product = this.lookupProduct(path[0].path);
    return {
      name: product.name,
      path: `/${product.clientCode}`,
      rooms: product.rooms.map(room => {
        return {
          name: room.name,
          path: `/${product.clientCode}/${room.clientCode}`
        };
      })
    }
  }

  public getRoomNav(path: UrlSegment[]): RoomNav {
    if (path[0].path == 'print') {
      path.shift();
    }
    const product = this.lookupProduct(path[0].path);
    const room = product.rooms.find(r => r.clientCode === path[1].path) ?? {} as Room;
    return {
      name: room.name,
      path: `/${product.clientCode}/${room.clientCode}`,
      pages: room.pages.map(page => {
        return {
          name: page.name,
          path: `/${product.clientCode}/${room?.clientCode}/${page.clientCode}`
        };
      })
    }
  }
}
